import React, { useEffect, useMemo, useRef } from 'react';
import ReactModal from 'react-modal';

import { getGlobalStyles } from '@tanium/coreui-mixins';
import {
  devWarning,
  getRgbString,
  UnsafeSpreadProps,
  ZINDEX_MODAL_CONTENT,
  ZINDEX_MODAL_OVERLAY,
} from '@tanium/coreui-utils';
import { useAppElementSelector } from '@tanium/react-app-element-context';
import styled, { css as cssEmotion } from '@tanium/react-emotion-9';
import { useTheme } from '@tanium/react-theme-context';
import { isDarkMode } from '@tanium/react-theme-definition';

import { OverlayFill } from './themeDefinitions';

export interface ModalBaseProps {
  /**
   * Whether the modal should be visible or not.
   */
  isOpen: boolean;
  /**
   * A callback invoked when the modal itself wants to be closed, such as when
   * the user presses the escape key.
   */
  onRequestClose?(event: React.MouseEvent | React.KeyboardEvent): void;
}

export interface Props extends ModalBaseProps, UnsafeSpreadProps {
  /**
   * The content to display in the modal.
   */
  children?: React.ReactNode;
}

const bodyStyle = cssEmotion({
  overflow: 'hidden',
});

const slideTransitionTimeMs = 300;
const fadeTransitionTimeMs = 150;
const componentCloseTimeoutMs = Math.max(slideTransitionTimeMs, fadeTransitionTimeMs);

const overlayHiddenStyleProps = {
  backgroundColor: 'rgba(0, 0, 0, 0)',
};

const overlayStyle = cssEmotion(
  {
    zIndex: ZINDEX_MODAL_OVERLAY,
    position: 'fixed',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
  },
  overlayHiddenStyleProps,
);

const overlayAfterOpenStyle = (shouldUseDarkModeOpacity: boolean) => {
  const overlayOpacity = shouldUseDarkModeOpacity ? '90%' : '40%';
  return cssEmotion({
    backgroundColor: getRgbString(OverlayFill(), overlayOpacity),
    transition: `background ${fadeTransitionTimeMs}ms linear`,
  });
};

const overlayBeforeCloseStyle = cssEmotion(overlayHiddenStyleProps);

const ModalChild = styled('div')(getGlobalStyles);

const modalHiddenStyleProps = {
  opacity: 0,
  [`${ModalChild}`]: {
    transform: 'translate(0, -25%)',
  },
};

const modalStyle = cssEmotion(
  {
    zIndex: ZINDEX_MODAL_CONTENT,
    display: 'block',
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'fixed',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    outline: 'none',
    [`${ModalChild}`]: {
      transition: `transform ${slideTransitionTimeMs}ms ease-out`,
    },
  },
  modalHiddenStyleProps,
);

const modalAfterOpenStyle = cssEmotion({
  transition: `opacity ${fadeTransitionTimeMs}ms linear`,
  opacity: 1,
  [`${ModalChild}`]: {
    transform: 'translate(0, 0)',
  },
});

const modalBeforeCloseStyle = cssEmotion(modalHiddenStyleProps);

/**
 * Fires a devWarning if the given appElement is an invalid selector (can't be found in the document).
 * Returns the referenced DOM element if valid.
 */
const validateAppElement = (appElement: undefined | string | HTMLElement): Element | undefined => {
  if (typeof appElement !== 'string') {
    return appElement;
  }

  const matchingElement = document.querySelector(appElement);
  devWarning(!!matchingElement, `Element ${appElement} not found.`);

  return matchingElement || undefined;
};

/**
 * A basic modal component that displays a minimally styled container. This
 * component is suitable when the contents of the modal must be highly
 * customized. Otherwise, consider using a Modal or another component.
 */
const ModalBase = ({ isOpen, onRequestClose, children, __UNSAFE_ADDITIONAL_PROPS__ }: Props) => {
  const theme = useTheme();
  const shouldUseDarkModeOpacity = useMemo(() => isDarkMode(theme), [theme]);

  const appElement = useAppElementSelector();

  const ariaHideApp = process.env.NODE_ENV !== 'test';
  const validatedAppElement = useMemo(() => validateAppElement(appElement), [appElement]);

  if (process.env.NODE_ENV !== 'production') {
    // The above condition is invariant
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    // The above condition is invariant
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(
      () => () => {
        devWarning(
          !isOpenRef.current,
          'A modal was removed from the page while it was still open. You should hide a modal by setting `isOpen={false}` instead of removing the modal from the page altogether.\n\nIf you just navigated away from a page that was displaying a modal, you can ignore this message.',
        );
      },
      [],
    );
  }

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      appElement={validatedAppElement}
      ariaHideApp={ariaHideApp}
      bodyOpenClassName={bodyStyle}
      overlayClassName={{
        base: overlayStyle,
        afterOpen: overlayAfterOpenStyle(shouldUseDarkModeOpacity),
        beforeClose: overlayBeforeCloseStyle,
      }}
      className={{
        base: modalStyle,
        afterOpen: modalAfterOpenStyle,
        beforeClose: modalBeforeCloseStyle,
      }}
      closeTimeoutMS={componentCloseTimeoutMs}
      {...__UNSAFE_ADDITIONAL_PROPS__}
    >
      <ModalChild>{children}</ModalChild>
    </ReactModal>
  );
};

export default ModalBase;
